import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import BlueprintBackground from "../components/BlueprintBackground";
import BrandLogo from "../components/BrandLogo";
import { reactivateByToken } from "../lib/api";

const TOKEN_KEY = "planflow.auth_token";
const USER_KEY = "planflow.auth_user";

/**
 * /reactivate?token=... — public landing page hit by the link in the
 * "Reactivate your Steeltooth account" email. POSTs the token to
 * /auth/reactivate, stashes the returned JWT, then offers a CTA into
 * the dashboard.
 */
export default function ReactivatePage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "loading", error: "", user: null });

    useEffect(() => {
        const token = (params.get("token") || "").trim();
        if (!token) {
            setState({
                status: "error",
                error: "Missing reactivation token. Use the link from your email.",
                user: null,
            });
            return;
        }
        (async () => {
            try {
                const { access_token, user } = await reactivateByToken(token);
                try {
                    localStorage.setItem(TOKEN_KEY, access_token);
                    localStorage.setItem(USER_KEY, JSON.stringify(user));
                } catch {}
                setState({ status: "ok", error: "", user });
            } catch (err) {
                setState({
                    status: "error",
                    error:
                        err?.response?.data?.detail ||
                        "This reactivation link is invalid or expired.",
                    user: null,
                });
            }
        })();
    }, [params]);

    return (
        <div className="relative h-[100dvh] w-full overflow-y-auto" data-testid="reactivate-page">
            <BlueprintBackground />
            <div className="relative z-10 min-h-full w-full flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-md">
                    <div className="flex justify-center mb-4">
                        <BrandLogo height={32} />
                    </div>
                    <div className="glass rounded-xl p-6">
                        {state.status === "loading" && (
                            <div
                                data-testid="reactivate-loading"
                                className="flex flex-col items-center text-center text-slate-300"
                            >
                                <Loader2 className="w-8 h-8 text-[#00A3FF] animate-spin mb-3" />
                                <div className="text-sm">Reactivating your account…</div>
                            </div>
                        )}
                        {state.status === "ok" && (
                            <div
                                data-testid="reactivate-success"
                                className="flex flex-col items-center text-center"
                            >
                                <CheckCircle className="w-10 h-10 text-emerald-400 mb-3" />
                                <h1 className="text-xl font-bold text-white mb-1">
                                    Welcome back!
                                </h1>
                                <p className="text-sm text-slate-300 mb-5">
                                    Your account <strong>{state.user?.email}</strong> is
                                    active again. All your projects, templates and
                                    credits are intact.
                                </p>
                                <button
                                    onClick={() => navigate("/home", { replace: true })}
                                    data-testid="btn-reactivate-continue"
                                    className="inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold px-5 py-2.5 rounded-md text-sm"
                                >
                                    Continue to dashboard
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                        {state.status === "error" && (
                            <div
                                data-testid="reactivate-error"
                                className="flex flex-col items-center text-center"
                            >
                                <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
                                <h1 className="text-xl font-bold text-white mb-1">
                                    Couldn't reactivate
                                </h1>
                                <p className="text-sm text-slate-300 mb-5">
                                    {state.error}
                                </p>
                                <Link
                                    to="/"
                                    data-testid="btn-reactivate-home"
                                    className="inline-flex items-center gap-2 border border-[#2A3655] hover:border-[#00A3FF] text-slate-200 hover:text-white px-5 py-2.5 rounded-md text-sm"
                                >
                                    Back to sign in
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
